"use client";

import { activeBadgeIsGapForecast, verdictsWithGapForecast } from "./gap-forecast-verdict";
import type { GapForecastInfo } from "@/lib/conditions/gap-forecast-display";
import { Fragment, useMemo, useState } from "react";
import { useShallowSearchParam } from "@/lib/hooks/use-shallow-search-param";
import dynamic from "next/dynamic";
import type {
  NamedSupSportDetail,
  SurfDetail,
  SportDetail,
} from "@/lib/conditions/detail";
import type { ConditionNote } from "@/lib/conditions/condition-notes";
import type { NormalisedHour, StoredHourlyScore } from "@/lib/conditions/daily-obs-types";
import type { CoastalLocationConfig } from "@/lib/conditions/visualiser/coastal-types";
import { isFeatureFlagEnabled } from "@/lib/feature-flags";
import type { SurfScoredDay } from "@/lib/conditions/detail";
import type { SurfHourlyData } from "@/lib/conditions/surf-hourly-detail";
import type { AirQualityData } from "@/lib/conditions/air-quality";
import type { WaterQualityData } from "@/lib/conditions/water-quality";
import type { SurfReport } from '@/lib/conditions/visualiser/surf-reports';
import type { SatelliteWaterQuality } from "@/lib/conditions/satellite-water-quality";
import type { UniversalEnhancements, SurfEnhancements, WindEnhancements, DivingEnhancements, SwimEnhancements, SupEnhancements, SportBadge, SportEligibilityNotices } from "@/lib/conditions/unified-types";
import type { EngineSport } from "@/lib/conditions/engines/core/types";
import {
  scoreExplanationForDay,
  type SafetyExplanationSeverity,
  type SafetyFactorExplanation,
  type SportScoreExplanation,
} from "@/lib/conditions/factor-explanations";
import { dailyCrowdPressure } from "@/lib/conditions/crowd-prediction";
import { getSeasonPhase } from "@/lib/conditions/seasons";
import { buildSportCoastalMetrics } from "@/lib/conditions/metric-strip";
import { windQualityLabel } from "@/lib/conditions/surf-labels";
import { degToCompass } from "@/lib/conditions/scoring";
import { deriveCoastalBriefing } from "@/lib/conditions/local-briefing";
import type { SafetyHeadsUpInput, SafetySeverity } from "@/lib/conditions/safety-headsup";
import { orderLocationHouseSlots, type HouseId } from "@/lib/conditions/location-houses";

import { TrendBadge } from "@/app/_components/conditions/TrendBadge";
import { RarityBadge } from "@/app/_components/conditions/RarityBadge";
import { SportSelector } from "@/app/_components/conditions/SportSelector";
import { VerdictBlock, type VerdictSportData } from "@/app/_components/conditions/VerdictBlock";
import { SafetyHeadsUp } from "@/app/_components/conditions/SafetyHeadsUp";
import { HouseTabs } from "@/app/_components/conditions/HouseTabs";
import { WeekOutlook } from "@/app/_components/conditions/WeekOutlook";
import { SportHourlyTimeline } from "@/app/_components/conditions/SportHourlyTimeline";
import { ExpandableScene } from "@/app/_components/weather-scene/ExpandableScene";
import { parseSurfDaySummary, surfWeekDates } from '@/lib/conditions/visualiser/surf-diorama';
import { isWaterSceneSport } from '@/lib/conditions/visualiser/water-sports';
import { isSupProfileKey } from '@/lib/conditions/sport-profiles';
import { WaterQualitySection } from "@/app/_components/conditions/WaterQualitySection";
import { AirQualityPanel } from "@/app/_components/conditions/AirQualityPanel";
import { shouldShowAirQuality } from "@/lib/conditions/air-quality-visibility";
import type { LuckFactor } from "@/lib/conditions/luck";
import { SeasonOverview } from "@/app/_components/conditions/SeasonOverview";
import { SpotOverview } from "@/app/_components/conditions/SpotOverview";
import { SeasonHeatmap } from "@/app/_components/conditions/SeasonHeatmap";
import { DivingInsights } from "@/app/_components/conditions/insights/DivingInsights";
import type { ReactNode } from "react";
import { LocalBriefing } from "@/app/_components/conditions/LocalBriefing";
import type { CoastalBriefingInput, CoastalSport } from "@/lib/conditions/local-briefing";
import type { LocationMetadata } from "@/lib/conditions/unified-types";
import { DisclosurePanel } from "@/app/_components/conditions/DisclosurePanel";
import { SafetyDisclaimer } from "@/app/_components/SafetyDisclaimer";
import { FactorHeadlineSummary } from "@/app/_components/conditions/insights/FactorFirst";
import {
  buildCoastalTodayTimeline,
  coastalOutlookSafetyState,
  selectCoastalOutlookForecast,
  selectCoastalOutlookBestDate,
} from "./forecast-surface-transport";
import { briefingSafetyV2Fields } from "./briefing-safety-v2";
import { expandHeatmapDatasets, type CompactHeatmapDatasets } from "./coastal-client-payload";
import { resolveSlotSport } from "@/lib/conditions/sport-aware-slot";
import { CoastalSportFocus } from "@/app/_components/conditions/SportFocus";
import { ForecastStatus } from "@/app/_components/conditions/ForecastStatus";
import { SessionWindowSummary } from "@/app/_components/conditions/SessionWindowSummary";
import { WindUnitControl, useWindUnit } from "@/app/_components/conditions/WindUnitControl";
import { TideChart } from "@/app/_components/conditions/TideChart";
import { evidenceVerdict, forecastIsStale, forecastSafety, formatWind, upcomingForecast } from "@/lib/conditions/forecast-presentation";
import { offsetIsoDate } from "@/lib/conditions/spot-locality";
import { intlTimeZoneAt } from "@/lib/time-zone-rules";

const SurfInsights = dynamic(() => import("@/app/_components/conditions/insights/SurfInsights").then(m => m.SurfInsights), {
  ssr: false,
  loading: () => <div className="animate-pulse bg-border/20 rounded-[14px] h-[300px]" />,
});
const WindInsights = dynamic(() => import("@/app/_components/conditions/insights/WindInsights").then(m => m.WindInsights), {
  ssr: false,
  loading: () => <div className="animate-pulse bg-border/20 rounded-[14px] h-[300px]" />,
});
const FoilInsights = dynamic(() => import("@/app/_components/conditions/insights/FoilInsights").then(m => m.FoilInsights), {
  ssr: false,
  loading: () => <div className="animate-pulse bg-border/20 rounded-[14px] h-[300px]" />,
});
const SwimInsights = dynamic(() => import("@/app/_components/conditions/insights/SwimInsights").then(m => m.SwimInsights), {
  ssr: false,
  loading: () => <div className="animate-pulse bg-border/20 rounded-[14px] h-[300px]" />,
});
const SupInsights = dynamic(() => import("@/app/_components/conditions/insights/SupInsights").then(m => m.SupInsights), {
  ssr: false,
  loading: () => <div className="animate-pulse bg-border/20 rounded-[14px] h-[300px]" />,
});
const BodyboardInsights = dynamic(() => import("@/app/_components/conditions/insights/BodyboardInsights").then(m => m.BodyboardInsights), {
  ssr: false,
  loading: () => <div className="animate-pulse bg-border/20 rounded-[14px] h-[300px]" />,
});

/* ─── Helpers ─── */

function toTimeLabel(hour: number) {
  const h = hour % 24;
  const suffix = h < 12 ? "am" : "pm";
  const display = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${display}:00${suffix}`;
}

const COASTAL_SPORTS_LIST = [
  "surf", "wind", "foil", "downwind-foil", "wing-foil",
  "bodyboarding", "diving", "swim", "sup", "sup-touring", "sup-surf", "sup-downwind",
] as const;

const COASTAL_SPORTS = new Set<string>(COASTAL_SPORTS_LIST);

/* ─── Props ─── */

type UnifiedCoastalProps = {
  surfDetail: SurfDetail;
  windDetail: SportDetail | null;
  foilDetail: SportDetail | null;
  downwindFoilDetail: SportDetail | null;
  wingFoilDetail: SportDetail | null;
  divingDetail: SportDetail | null;
  swimDetail: SportDetail | null;
  supDetail: SportDetail | null;
  supTouringDetail: NamedSupSportDetail | null;
  supSurfDetail: NamedSupSportDetail | null;
  supDownwindDetail: NamedSupSportDetail | null;
  bodyboardingDetail: SportDetail | null;
  conditionNotes: Record<string, ConditionNote>;
  /** Packed on the server (coastal-client-payload.ts) and unpacked here. */
  heatmapDatasets: CompactHeatmapDatasets;
  hourlyJsonb: NormalisedHour[] | null;
  yesterdayJsonb: NormalisedHour[] | null;
  tomorrowJsonb: NormalisedHour[] | null;
  spotName: string;
  visualiserLocation: CoastalLocationConfig;
  longitude: number;
  timezone?: string | null;
  todayISO: string;
  latitude?: number | null;
  availableSports: string[];
  badges: SportBadge[];
  /** The nightly gap: the badges carry the previous run's forecast for today (lib/conditions/gap-forecast.ts). */
  gapForecast?: GapForecastInfo | null;
  defaultSport: string;
  surfForecast: SurfScoredDay[];
  bestForecastDate: string | null;
  busyPeriodLabel: string | null;
  isBusyPeriod: boolean;
  beachFacingDeg: number | null;
  bottomType: string | null;
  lifeguarded: string | null;
  maxWaveSize: string | null;
  difficultyLevel: string | null;
  locationMetadata: LocationMetadata | null;
  nearbyCsoCount: number;
  hourlyData: SurfHourlyData | null;
  sportHourlyScores: Record<string, StoredHourlyScore[]>;
  sportScoreExplanations: Record<
    string,
    SportScoreExplanation<SafetyFactorExplanation<SafetyExplanationSeverity>>
  >;
  yesterdaySportScores: Record<string, StoredHourlyScore[]>;
  tomorrowSportScores: Record<string, StoredHourlyScore[]>;
  airQuality: AirQualityData | null;
  waterQuality: WaterQualityData | null;
  coastalReports?: readonly SurfReport[];
  satelliteWQ: SatelliteWaterQuality | null;
  swellDelta: number | null;
  universalEnhancements: Record<string, UniversalEnhancements>;
  surfEnhancements: SurfEnhancements | null;
  windEnhancements: WindEnhancements | null;
  divingEnhancements: DivingEnhancements | null;
  swimEnhancements: SwimEnhancements | null;
  supEnhancements: SupEnhancements | null;
  description: string | null;
  weatherCode: number | null;
  /** Server-rendered (non sport-aware) sections, placed into the house panels. */
  forecastPanelSlot?: ReactNode;
  spotLocalSlot?: ReactNode;
  /**
   * The area block (verdict line and the one open "Better nearby" list, which hosts the session
   * finder), placed directly under the verdict. It replaces the old "Nearby spots" disclosure.
   */
  areaSlot?: ReactNode;
  spotCommunitySlot?: ReactNode;
  seasonPanelSlot?: ReactNode;
  publicHolidayDates: string[];
  /**
   * Sports CONDITIONS_SAFETY_POLICY_V2_SPORTS lists (server-only, so passed as data). Absent while
   * the flag is unset: every briefing then runs its legacy rules.
   */
  safetyPolicyV2Sports?: readonly EngineSport[];
  /** Why a shown sport is not ranked here (curated venue eligibility); absent unless the flag lists one. */
  sportEligibilityNotices?: SportEligibilityNotices;
  /** Server-rendered evidence lines under each sport's score; absent unless one has something to show. */
  sportEvidenceSlots?: Partial<Record<string, ReactNode>>;
};

/* ─── Component ─── */

export function UnifiedCoastal({
  surfDetail,
  windDetail,
  foilDetail,
  downwindFoilDetail,
  wingFoilDetail,
  divingDetail,
  swimDetail,
  supDetail,
  supTouringDetail,
  supSurfDetail,
  supDownwindDetail,
  bodyboardingDetail,
  heatmapDatasets,
  hourlyJsonb,
  yesterdayJsonb,
  tomorrowJsonb,
  spotName,
  visualiserLocation,
  longitude,
  timezone,
  todayISO,
  latitude,
  availableSports,
  badges,
  gapForecast = null,
  defaultSport,
  surfForecast,
  bestForecastDate,
  busyPeriodLabel,
  isBusyPeriod,
  beachFacingDeg,
  bottomType,
  lifeguarded,
  maxWaveSize,
  difficultyLevel,
  locationMetadata,
  nearbyCsoCount,
  hourlyData,
  sportHourlyScores,
  sportScoreExplanations,
  yesterdaySportScores,
  tomorrowSportScores,
  airQuality,
  waterQuality,
  coastalReports,
  satelliteWQ,
  swellDelta,
  universalEnhancements,
  surfEnhancements,
  windEnhancements,
  divingEnhancements,
  swimEnhancements,
  supEnhancements,
  description,
  weatherCode: _weatherCode,
  forecastPanelSlot,
  spotLocalSlot,
  areaSlot,
  spotCommunitySlot,
  seasonPanelSlot,
  publicHolidayDates,
  safetyPolicyV2Sports,
  sportEligibilityNotices,
  sportEvidenceSlots,
}: UnifiedCoastalProps) {
  // Post-hydration param read keeps this statically rendered page's full
  // HTML in the initial response (useSearchParams would swap it for the
  // Suspense fallback)
  const sportParam = useShallowSearchParam("sport");
  const urlSport = sportParam ?? defaultSport;
  const activeSport = resolveSlotSport(urlSport, defaultSport, availableSports);

  // Day toggle (yesterday / today / tomorrow)
  type DayKey = "yesterday" | "today" | "tomorrow";
  const [activeDay, setActiveDay] = useState<DayKey>("today");
  const dayHourlyMap: Record<DayKey, NormalisedHour[] | null> = {
    yesterday: yesterdayJsonb,
    today: hourlyJsonb,
    tomorrow: tomorrowJsonb,
  };
  const dayScoresMap: Record<DayKey, Record<string, StoredHourlyScore[]>> = {
    yesterday: yesterdaySportScores,
    today: sportHourlyScores,
    tomorrow: tomorrowSportScores,
  };
  const activeHourly = dayHourlyMap[activeDay];
  const activeScores = dayScoresMap[activeDay];
  const hasDayToggle = !!yesterdayJsonb || !!tomorrowJsonb;
  const todayTimeline = buildCoastalTodayTimeline({
    activeSport,
    sportHourlyScores,
    hourlyData,
  });

  // Local time from longitude (hours in data are already in location local time). The zone comes
  // from intlTimeZoneAt so a browser whose data predates tzdata 2026e still shows today's wall clock.
  const nowForLocal = new Date();
  const localParts = timezone ? new Intl.DateTimeFormat("en-GB", {
    timeZone: intlTimeZoneAt(timezone, nowForLocal.getTime()), hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(nowForLocal) : null;
  const utcOffsetHours = Math.round(longitude / 15);
  const localHour = localParts
    ? Number(localParts.find((part) => part.type === "hour")?.value ?? 0) % 24
    : (new Date().getUTCHours() + utcOffsetHours + 24) % 24;
  const localMinute = localParts
    ? Number(localParts.find((part) => part.type === "minute")?.value ?? 0)
    : new Date().getUTCMinutes();
  const localTimeLabel = `${localHour % 12 === 0 ? 12 : localHour % 12}:${localMinute.toString().padStart(2, "0")}${localHour < 12 ? "am" : "pm"}`;
  const coastalWaterEnabled = isFeatureFlagEnabled("coastalWater3d");
  const visualiserDateKeys = {
    today: todayISO,
    tomorrow: offsetIsoDate(todayISO, 1),
  };

  // Unpacked once per payload, so each dataset keeps its identity across sport switches.
  const allHeatmapDatasets = useMemo(() => expandHeatmapDatasets(heatmapDatasets), [heatmapDatasets]);
  const activeHeatmapDatasets = allHeatmapDatasets.filter((ds) => ds.key === activeSport);

  function detailFor(sport: string): SportDetail | NamedSupSportDetail | null {
    switch (sport) {
      case "wind": return windDetail;
      case "foil": return foilDetail;
      case "downwind-foil": return downwindFoilDetail;
      case "wing-foil": return wingFoilDetail;
      case "diving": return divingDetail;
      case "swim": return swimDetail;
      case "sup": return supDetail;
      case "sup-touring": return supTouringDetail;
      case "sup-surf": return supSurfDetail;
      case "sup-downwind": return supDownwindDetail;
      case "bodyboarding": return bodyboardingDetail;
      default: return null;
    }
  }

  const sportDetail = detailFor(activeSport);
  const isSurf = activeSport === "surf";
  const windUnit = useWindUnit();
  const SPORT_LABELS: Record<string, string> = { "sup-touring": "Touring SUP", "sup-surf": "SUP Surf", "sup-downwind": "Downwind SUP" };
  const activeUpdated = isSurf ? surfDetail.lastUpdated : sportDetail?.lastUpdated ?? surfDetail.lastUpdated;
  const activeStale = forecastIsStale(activeUpdated, isSurf ? surfDetail.isStale : sportDetail?.isStale ?? surfDetail.isStale);
  const activeEnhancements = universalEnhancements[activeSport] ?? null;
  // Per-sport luck map passed to VerdictBlock for the inline chip.
  const perSportLuck: Record<string, LuckFactor | null> = {
    surf: surfDetail.luck,
    wind: detailFor("wind")?.luck ?? null,
    foil: detailFor("foil")?.luck ?? null,
    "downwind-foil": detailFor("downwind-foil")?.luck ?? null,
    "wing-foil": detailFor("wing-foil")?.luck ?? null,
    diving: detailFor("diving")?.luck ?? null,
    swim: detailFor("swim")?.luck ?? null,
    sup: detailFor("sup")?.luck ?? null,
    "sup-touring": detailFor("sup-touring")?.luck ?? null,
    "sup-surf": detailFor("sup-surf")?.luck ?? null,
    "sup-downwind": detailFor("sup-downwind")?.luck ?? null,
    bodyboarding: detailFor("bodyboarding")?.luck ?? null,
  };
  const hasWaterQuality = waterQuality !== null || satelliteWQ !== null;
  const hasAirQuality = shouldShowAirQuality(airQuality);
  const hasEnvironmental = hasAirQuality || hasWaterQuality;

  const currentHour = hourlyJsonb
    ? (() => {
        return hourlyJsonb.find(h => h.hour >= localHour) ?? hourlyJsonb[0] ?? null;
      })()
    : null;

  const activePackingNotes = isSurf ? surfDetail.packingNotes : (sportDetail?.packingNotes ?? []);
  const currentExplanation = sportScoreExplanations[activeSport] ?? null;

  const sunTimes = activeHourly && activeHourly.length > 0 ? (() => {
    const rise = activeHourly.findIndex((h, i) => i > 0 && h.weather.is_day && !activeHourly[i - 1].weather.is_day);
    const set = activeHourly.findIndex((h, i) => i > 0 && !h.weather.is_day && activeHourly[i - 1].weather.is_day);
    return { sunrise: rise >= 0 ? activeHourly[rise].hour : null, sunset: set >= 0 ? activeHourly[set].hour : null };
  })() : null;

  // Shared marine snapshot for the whole spot (swell/wind/water come from the
  // surf marine reading regardless of sport; only the sport varies which
  // hazards apply and how the conditions read).
  // True trailing 24h ending at the current local hour: yesterday's hours
  // after now, plus today's hours up to now. (hourlyJsonb/yesterdayJsonb are
  // full calendar-day arrays, so a plain slice would count future forecast
  // rain and drop yesterday.)
  const trailingRain24h = [
    ...(yesterdayJsonb ?? []).filter((h) => h.hour > localHour),
    ...(hourlyJsonb ?? []).filter((h) => h.hour <= localHour),
  ].reduce((s, h) => s + (h.weather.rain_mm ?? h.weather.precip_mm ?? 0), 0);
  const stormActive = (hourlyJsonb ?? []).some(
    (h) => h.weather.is_day && (h.weather.cape_jkg ?? 0) > 1000 && (h.weather.lifted_index ?? 5) < -2
  );

  function briefingInputFor(sport: string): CoastalBriefingInput | null {
    if (!COASTAL_SPORTS.has(sport)) return null;
    return {
      sport: sport as CoastalSport,
      waterTempC: surfDetail.raw.waterTempC ?? null,
      sstC: surfDetail.raw.waterTempC ?? null,
      month: Number(todayISO.slice(5, 7)),
      swellHeightM: surfDetail.raw.swellWaveHeightM ?? 0,
      windSpeedKmh: surfDetail.raw.windSpeedKmh ?? 0,
      windDirDeg: surfDetail.raw.windDirectionDeg ?? null,
      beachFacingDeg,
      lowTideHours: (hourlyData?.tide?.lowTides ?? []).map((t) => t.hour),
      lowTideTimeKnown: hourlyData?.tide?.datum !== "none",
      bottomType,
      seabed: locationMetadata?.seabed ?? null,
      nearbyCsoCount,
      trailingRain24h,
      maxWaveSize,
      difficultyLevel,
      lifeguarded,
      stormActive,
      locationMetadata,
      // Safety policy v2 (P2C-WP9-04): only for a sport the server says the flag lists.
      ...briefingSafetyV2Fields(sport, safetyPolicyV2Sports ?? [], {
        windSpeedKmh: surfDetail.raw.windSpeedKmh,
        hours: hourlyJsonb,
        currentHour,
      }),
    };
  }

  const activeBriefingInput = activeSport.startsWith("sup-")
    ? null
    : briefingInputFor(activeSport);

  /* ─── Per-sport verdict + safety maps (consumed by the shell) ─── */

  // Coastal verdict metrics: surf reads its own marine snapshot; the other
  // sports read the flat SportDetail fields. Swell direction is a spot-level
  // property, so non-surf sports fall back to the shared marine reading.
  function metricsFor(sport: string): VerdictSportData["metrics"] {
    const swellDirDeg = surfDetail.raw.swellDirectionDeg ?? null;
    const swellDirLabel = swellDirDeg != null ? degToCompass(swellDirDeg) : null;
    const d = detailFor(sport);
    const r = surfDetail.raw;
    const tideNow = hourlyData?.tide?.points.find((point) => point.hour === localHour);
    const windSpeed = sport === "surf" ? r.windSpeedKmh : d?.windSpeedKmh;
    const direction = sport === "surf" ? r.windDirectionDeg : d?.windDirectionDeg;
    return buildSportCoastalMetrics({
      sport,
      waveHeightM: sport === "surf" ? r.swellWaveHeightM : d?.waveHeightM ?? null,
      swellPeriodS: sport === "surf" ? r.swellWavePeriodS : d?.swellPeriodS ?? null,
      swellDirLabel,
      windSpeedKmh: windSpeed ?? null,
      windLabel: direction != null ? windQualityLabel(direction, beachFacingDeg).label : null,
      windText: formatWind(windSpeed, windUnit),
      gustText: formatWind(d?.windGustKmh ?? currentHour?.weather.wind_gust_10m_kmh, windUnit),
      waterTempC: sport === "surf" ? r.waterTempC : d?.waterTempC ?? null,
      tideLabel: tideNow ? `${tideNow.state[0].toUpperCase()}${tideNow.state.slice(1)}` : null,
      tideApproximate: hourlyData?.tide?.isApproximate,
      secchiDepthM: d?.secchiDepthM,
      currentVelocityMs: d?.currentVelocityMs,
      windWaveHeightM: currentHour?.marine?.wind_wave_height_m,
      windWaveDirection: currentHour?.marine?.wind_wave_dir_deg != null ? `From ${degToCompass(currentHour.marine.wind_wave_dir_deg)} · hourly` : null,
    });
  }

  const allSports = COASTAL_SPORTS_LIST;
  const perSportVerdict: Record<string, VerdictSportData> = {};
  const perSportSafety: Record<string, SafetyHeadsUpInput> = {};
  for (const sport of allSports) {
    const isSurfSport = sport === "surf";
    const d = isSurfSport ? null : detailFor(sport);
    if (!isSurfSport && !d) {
      perSportVerdict[sport] = { score: null, label: null, note: null, metrics: metricsFor(sport), tacticalLine: null, unavailableReason: "A current score for this sport is unavailable." };
      perSportSafety[sport] = { hazards: [], dataState: "missing", checksIncomplete: true };
      continue;
    }
    const eligibilityNotice = sportEligibilityNotices?.[sport];
    const evidenceSlot = sportEvidenceSlots?.[sport];
    perSportVerdict[sport] = {
      label: isSurfSport ? surfDetail.conditionsLabel : d!.label,
      score: isSurfSport ? surfDetail.conditionsScore : d!.score,
      note: evidenceVerdict(sportScoreExplanations[sport]) ?? null,
      metrics: metricsFor(sport),
      tacticalLine: null,
      ...(eligibilityNotice ? { eligibilityNotice } : {}),
      ...(evidenceSlot ? { evidenceSlot } : {}),
    };
    const input = briefingInputFor(sport);
    if (sport.startsWith("sup-") && d) {
      const canonical = d.namedSupSafety;
      const hasCanonicalHazard = canonical?.some(({ severity }) =>
        severity === "red" || severity === "amber"
      ) ?? false;
      perSportSafety[sport] = canonical === null || canonical === undefined
        ? { hazards: [], dataState: "missing" }
        : {
            hazards: canonical.flatMap((row) => {
              if (row.severity === "green") return [];
              const severity: SafetySeverity = row.severity === "red"
                ? "danger"
                : row.severity === "amber"
                  ? "caution"
                  : "info";
              return [{ severity, label: row.label }];
            }),
            dataState: !hasCanonicalHazard && canonical.some(
              ({ severity }) => severity === "unavailable",
            )
              ? "missing"
              : "present",
          };
    } else {
      perSportSafety[sport] = {
        hazards: input
          ? deriveCoastalBriefing(input).hazards.map((h) => ({ severity: h.severity as SafetySeverity, label: h.title }))
          : [],
      };
    }
    const stale = forecastIsStale(isSurfSport ? surfDetail.lastUpdated : d?.lastUpdated ?? surfDetail.lastUpdated, isSurfSport ? surfDetail.isStale : d?.isStale ?? surfDetail.isStale);
    perSportSafety[sport] = forecastSafety(perSportSafety[sport], sportScoreExplanations[sport], isSurfSport ? surfDetail.scoreEvidence : d?.scoreEvidence, stale);
    if (stale) {
      perSportVerdict[sport].score = null;
      perSportVerdict[sport].note = null;
      perSportVerdict[sport].unavailableReason = "Historical readings, awaiting a fresh forecast.";
      perSportLuck[sport] = null;
    }

  }

  /* ─── Forecast house: sport-aware client sections ─── */

  const showHourlyForecast = !activeStale && (activeDay === 'today'
    ? !!todayTimeline && !!hourlyJsonb?.length
    : !!activeScores[activeSport] && !!activeHourly?.length);

  const forecastPanel = (
    <div className="py-8 md:py-10">
      {orderLocationHouseSlots<"forecast", ReactNode>("forecast", {
      "today-hourly": (
        <>
      <div id="section-hourly" data-house-section="today-hourly" className="scroll-mt-28">
        {showHourlyForecast && (
          <div className="bg-white rounded-[18px] border border-border-light p-5 md:p-7">
            <div className="flex items-baseline justify-between mb-1">
              <h3 className="font-serif text-[18px] md:text-[22px] font-bold text-navy">
                {activeDay === "today" ? "Today's forecast" : activeDay === "yesterday" ? "Yesterday" : "Tomorrow"}
              </h3>
              <span className="text-[12px] text-text-muted">
                Local time {localTimeLabel}
              </span>
            </div>
            <SessionWindowSummary
              duration={activeSport === "surf" ? 3 : 2}
              scores={activeDay === "today" ? todayTimeline?.scores ?? [] : activeScores[activeSport] ?? []}
              localHour={activeDay === "today" ? localHour + localMinute / 60 : undefined}
              blocked={activeStale || perSportSafety[activeSport]?.dataState !== "present" || perSportSafety[activeSport]?.checksIncomplete || perSportSafety[activeSport]?.hazards.some(({ severity }) => severity === "danger")}
            />
            {hasDayToggle && (
              <div className="flex gap-1.5 mb-4" role="group" aria-label="Select day">
                {([["yesterday", "Yesterday"], ["today", "Today"], ["tomorrow", "Tomorrow"]] as const).map(([key, label]) => {
                  const hasData = key === "today"
                    ? !!(hourlyData?.hours.length || hourlyJsonb?.length)
                    : !!dayHourlyMap[key]?.length;
                  return (
                    <button
                      key={key}
                      type="button"
                      aria-pressed={activeDay === key}
                      onClick={() => hasData && setActiveDay(key)}
                      disabled={!hasData}
                      className={`min-h-11 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal px-3.5 py-1.5 rounded-full text-[12px] font-semibold transition-colors ${
                        activeDay === key
                          ? "bg-navy text-white"
                          : hasData
                            ? "bg-warm-bg text-text-muted hover:bg-stone-200"
                            : "bg-warm-bg text-text-muted/40 cursor-not-allowed"
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            )}
            {activeDay === "today" ? (
              todayTimeline && hourlyJsonb ? (
                <SportHourlyTimeline
                  scores={todayTimeline.scores}
                  hours={hourlyJsonb}
                  sport={todayTimeline.sport}
                  localHour={localHour}
                  windUnit={windUnit}
                />
              ) : null
            ) : activeScores[activeSport] && activeHourly ? (
              <SportHourlyTimeline scores={activeScores[activeSport]} hours={activeHourly} sport={activeSport} windUnit={windUnit} />
            ) : null}
          </div>
        )}

        {(activeSport === 'surf' || isWaterSceneSport(activeSport) || (showHourlyForecast && coastalWaterEnabled && !!hourlyJsonb?.length)) && (
          <div className="mt-5">
            <ExpandableScene
              todayHours={hourlyJsonb}
              tomorrowHours={tomorrowJsonb}
              currentHour={localHour}
              locationType="surf"
              sport={activeSport}
              locationMeta={{
                name: spotName,
                slug: visualiserLocation.slug,
                country: surfDetail.country,
                region: surfDetail.region,
                lat: latitude ?? 45,
                beachFacingDeg: visualiserLocation.beachFacingDeg,
                beachGradient: visualiserLocation.beachGradient,
                breakType: visualiserLocation.breakType,
                exposedSectors: visualiserLocation.exposedSectors,
                shelterLevel: visualiserLocation.shelterLevel,
              }}
              dateKeys={visualiserDateKeys}
              allowedSupProfiles={availableSports.filter(isSupProfileKey)}
              waterForecast={isWaterSceneSport(activeSport) ? {
                reports: coastalReports,
                location: { ...visualiserLocation, name: spotName, country: surfDetail.country, region: surfDetail.region,
                  lat: latitude ?? Number.NaN, lng: longitude ?? Number.NaN, timezone: timezone ?? null },
                updatedAt: surfDetail.lastUpdated,
                days: surfWeekDates(visualiserDateKeys.today).map((date, index) => ({
                  date,
                  hours: index === 0 ? hourlyJsonb ?? [] : index === 1 ? tomorrowJsonb ?? [] : [],
                  // These page props have no hourly issue timestamp or exact stored
                  // score provenance. Shared weather remains useful without a rating.
                  updatedAt: index < 2 ? null : surfDetail.lastUpdated,
                  sportHourlyScores: {},
                  tidePoints: index === 0 ? hourlyData?.tide?.points ?? null : null,
                  waterQuality: index === 0 && satelliteWQ?.date === date
                    ? { secchiDepthM: satelliteWQ.secchiDepthM, chlorophyllMgM3: satelliteWQ.chlorophyllMgM3 } : null,
                  summary: parseSurfDaySummary(surfDetail.forecast.find((day) => day.date === date)),
                })),
              } : undefined}
              surfForecast={activeSport === 'surf' ? {
                reports: coastalReports,
                location: { ...visualiserLocation, name: spotName, country: surfDetail.country, region: surfDetail.region,
                  lat: latitude ?? 0, lng: longitude ?? 0, timezone: timezone ?? null },
                updatedAt: surfDetail.lastUpdated,
                days: surfWeekDates(visualiserDateKeys.today).map((date, index) => ({
                  date,
                  hours: index === 0 ? hourlyJsonb ?? [] : index === 1 ? tomorrowJsonb ?? [] : [],
                  score: surfDetail.isStale ? null : surfDetail.forecast.find((day) => day.date === date)?.score ?? null,
                  hourlyScores: surfDetail.isStale ? [] : index === 0 ? sportHourlyScores.surf ?? [] : index === 1 ? tomorrowSportScores.surf ?? [] : [],
                  waterQuality: index === 0 && satelliteWQ?.date === date
                    ? { secchiDepthM: satelliteWQ.secchiDepthM, chlorophyllMgM3: satelliteWQ.chlorophyllMgM3 } : null,
                  summary: parseSurfDaySummary(surfDetail.forecast.find((day) => day.date === date)),
                })),
              } : undefined}
              tidePoints={hourlyData?.tide?.points ?? null}
              sportScore={perSportVerdict[activeSport]?.score !== null && perSportVerdict[activeSport]?.score !== undefined
                ? {
                    score: perSportVerdict[activeSport].score,
                    label: perSportVerdict[activeSport].label ?? "Label unavailable",
                  }
                : null}
              hourlyScores={sportHourlyScores[activeSport]?.flatMap((point) =>
                typeof point.score === "number"
                  ? [{ hour: point.hour, score: point.score }]
                  : [],
              )}
              waterQuality={satelliteWQ
                ? { secchiDepthM: satelliteWQ.secchiDepthM, chlorophyllMgM3: satelliteWQ.chlorophyllMgM3 }
                : null}
            />
          </div>
        )}

        {!activeStale && activeDay === "today" && hourlyData?.tide && !["wind", "wing-foil", "swim"].includes(activeSport) && <div className="mt-4"><TideChart tide={hourlyData.tide} tidePreference={hourlyData.tidePreference ?? null} /></div>}
        {!activeStale && sunTimes && (sunTimes.sunrise !== null || sunTimes.sunset !== null) && (
          <div className="flex items-center gap-6 text-[13px] text-text-muted mt-3">
            {sunTimes.sunrise !== null && (
              <span className="flex items-center gap-1.5">
                <span aria-hidden className="text-amber-400">{"🌅"}</span>
                <span className="font-medium text-navy">{toTimeLabel(sunTimes.sunrise)}</span>
                <span>sunrise</span>
              </span>
            )}
            {sunTimes.sunset !== null && (
              <span className="flex items-center gap-1.5">
                <span aria-hidden className="text-orange-400">{"🌇"}</span>
                <span className="font-medium text-navy">{toTimeLabel(sunTimes.sunset)}</span>
                <span>sunset</span>
              </span>
            )}
          </div>
        )}
      </div>
        </>
      ),

      "factor-explanation": !activeStale && activeHourly && activeHourly.length > 0 ? (
        <div data-house-section="factor-explanation">
          <DisclosurePanel title="Why this score" id="section-insights">
          {activeSport === "surf" && (
            <SurfInsights
              hourlyData={activeHourly}
              surfDetail={surfDetail}
              beachFacingDeg={beachFacingDeg}
              tidePreference={hourlyData?.tidePreference ?? null}
              tidePoints={hourlyData?.tide?.points ?? null}
              tide={hourlyData?.tide ?? null}
              swellDelta={swellDelta}
              topTags={hourlyData?.topTags ?? []}
              surfEnhancements={surfEnhancements}
              sessionWindow={activeDay === "today" ? (activeEnhancements?.sessionWindow ?? null) : null}
            />
          )}
          {activeSport === "wind" && windDetail && (
            <WindInsights
              hourlyData={activeHourly}
              windDetail={windDetail}
              beachFacingDeg={beachFacingDeg}
              thermal={windEnhancements?.thermal}
              enhancements={windEnhancements}
              tide={hourlyData?.tide ?? null}
              tidePreference={null}
              explanation={scoreExplanationForDay(activeDay, sportScoreExplanations, "wind")}
            />
          )}
          {activeSport === "foil" && foilDetail && (
            <FoilInsights
              hourlyData={activeHourly}
              foilDetail={foilDetail}
              enhancements={null}
              tide={hourlyData?.tide ?? null}
              tidePreference={null}
              activeDiscipline="prone"
              explanation={scoreExplanationForDay(activeDay, sportScoreExplanations, "foil")}
            />
          )}
          {activeSport === "downwind-foil" && downwindFoilDetail && (
            <FoilInsights
              hourlyData={activeHourly}
              foilDetail={downwindFoilDetail}
              enhancements={null}
              tide={hourlyData?.tide ?? null}
              tidePreference={null}
              activeDiscipline="downwind"
              explanation={scoreExplanationForDay(activeDay, sportScoreExplanations, "downwind-foil")}
            />
          )}
          {activeSport === "wing-foil" && wingFoilDetail && (
            <FoilInsights
              hourlyData={activeHourly}
              foilDetail={wingFoilDetail}
              enhancements={null}
              tide={hourlyData?.tide ?? null}
              tidePreference={null}
              activeDiscipline="wing"
              explanation={scoreExplanationForDay(activeDay, sportScoreExplanations, "wing-foil")}
            />
          )}
          {activeSport === "diving" && divingDetail && (
            <DivingInsights
              hourlyData={activeHourly}
              divingDetail={divingDetail}
              latitude={latitude}
              enhancements={divingEnhancements}
              tide={hourlyData?.tide ?? null}
              tidePreference={hourlyData?.tidePreference ?? null}
              explanation={scoreExplanationForDay(activeDay, sportScoreExplanations, "diving")}
            />
          )}
          {activeSport === "swim" && swimDetail && (
            <SwimInsights
              hourlyData={activeHourly}
              swimDetail={swimDetail}
              enhancements={swimEnhancements}
              explanation={scoreExplanationForDay(activeDay, sportScoreExplanations, "swim")}
            />
          )}
          {activeSport === "sup" && supDetail && (
            <SupInsights
              hourlyData={activeHourly}
              supDetail={supDetail}
              enhancements={supEnhancements}
              tide={hourlyData?.tide ?? null}
              explanation={scoreExplanationForDay(activeDay, sportScoreExplanations, "sup")}
            />
          )}
          {(activeSport === "sup-touring" || activeSport === "sup-surf" || activeSport === "sup-downwind") && sportDetail && (
            <SupInsights
              hourlyData={activeHourly}
              supDetail={sportDetail}
              profile={activeSport}
              enhancements={null}
              tide={hourlyData?.tide ?? null}
              explanation={activeDay === "today" ? sportScoreExplanations[activeSport] ?? null : null}
            />
          )}
          {activeSport === "bodyboarding" && bodyboardingDetail && (
            <BodyboardInsights
              hourlyData={activeHourly}
              bodyboardDetail={bodyboardingDetail}
              beachFacingDeg={beachFacingDeg}
              tide={hourlyData?.tide ?? null}
              tidePreference={hourlyData?.tidePreference ?? null}
              explanation={scoreExplanationForDay(activeDay, sportScoreExplanations, "bodyboarding")}
            />
          )}
          </DisclosurePanel>
        </div>
      ) : null,

      tactical: !activeStale && activeEnhancements && (activeEnhancements.trend || activeEnhancements.rarity) ? (
        <div data-house-section="tactical" className="mt-6 flex flex-wrap items-center gap-2">
          <TrendBadge trend={activeEnhancements.trend} />
          <RarityBadge rarity={activeEnhancements.rarity} />
        </div>
      ) : null,

      outlook: (() => {
        const scoredForecast = upcomingForecast(selectCoastalOutlookForecast({
          activeSport,
          sportForecast: sportDetail?.forecast,
          surfForecast,
        }), todayISO).map((day) => day.date === todayISO && perSportVerdict[activeSport]?.score != null
          ? { ...day, score: perSportVerdict[activeSport].score!, label: perSportVerdict[activeSport].label ?? day.label }
          : day);
        const weatherOnly = scoredForecast.length === 0 || activeStale;
        const scoresByDate = new Map(scoredForecast.map((day) => [day.date, day]));
        const weatherByDate = new Map(upcomingForecast(surfForecast, todayISO).map((day) => [day.date, day]));
        const forecastDates = [...new Set([...weatherByDate.keys(), ...scoresByDate.keys()])].sort();
        const outlookDays = forecastDates.map((date) => {
          const rated = !activeStale ? scoresByDate.get(date) : undefined;
          const d = rated ?? weatherByDate.get(date) ?? scoresByDate.get(date)!;
          const forecastDate = new Date(d.date + "T00:00:00Z");
          const dow = forecastDate.getUTCDay();
          const seasonPhase = getSeasonPhase(d.date, `${forecastDate.getUTCFullYear()}-01-01`, `${forecastDate.getUTCFullYear()}-12-31`);
          const dailyCrowd = dailyCrowdPressure({
            schoolHolidayPressure: isBusyPeriod ? 0.5 : 0,
            isPublicHoliday: publicHolidayDates.includes(d.date),
            dayOfWeek: dow,
            seasonPhase,
          });
          const todayVerdict = !activeStale && date === todayISO ? perSportVerdict[activeSport] : undefined;
          const safetyState = rated ? coastalOutlookSafetyState(activeSport, rated) : "no-data" as const;
          return {
            date: d.date,
            score: todayVerdict?.score ?? rated?.score ?? null,
            label: todayVerdict?.label ?? rated?.label ?? "Sport score unavailable",
            weatherCode: d.weatherCode,
            swellHeightM: d.swellHeightM,
            swellPeriodS: d.swellPeriodS,
            windSpeedKmh: d.windSpeedKmh,
            windGustKmh: d.windGustKmh,
            waterTempC: d.waterTempC,
            currentVelocityMs: d.currentVelocityMs,
            crowd: dailyCrowd,
            ...(safetyState === undefined ? {} : { safetyState }),
          };
        });
        const best = selectCoastalOutlookBestDate({
          activeSport,
          forecast: weatherOnly ? [] : scoredForecast,
          surfBestDate: weatherOnly ? null : bestForecastDate,
        });

        return outlookDays.length > 0 ? (
          <div id="section-forecast" data-house-section="outlook" className="mt-8 md:mt-10 bg-white rounded-[18px] border border-border-light p-5 md:p-7 scroll-mt-28">
            <div className="flex items-baseline justify-between mb-5">
              <h3 className="font-serif text-[20px] md:text-[24px] font-bold text-navy">Next 7 days</h3>
              <span className="text-[12px] text-text-muted">
                {weatherOnly ? "Weather outlook" : "Forecast via Open-Meteo"}
              </span>
            </div>
            {weatherOnly && <p className="mb-4 text-[12px] leading-relaxed text-text-muted">A current {sportDetail?.displayName ?? SPORT_LABELS[activeSport] ?? activeSport} score outlook is unavailable for some dates. Days without a rating show shared weather and sea readings.{activeSport === "diving" && " A diving rating also needs suitable water-clarity evidence. Today’s satellite estimate is not a visibility forecast for the coming week."}</p>}
            {!weatherOnly && outlookDays.some((day) => day.score === null) && <p className="mb-4 text-[12px] text-text-muted">Unscored days retain the available weather forecast. A sport rating is unavailable for those dates.</p>}
            <WeekOutlook
              sport={activeSport}
              windUnit={windUnit}
              days={outlookDays}
              todayISO={todayISO}
              bestDate={best}
              busyPeriodLabel={busyPeriodLabel}
            />
          </div>
        ) : <p className="mt-6 rounded-[12px] border border-border bg-white p-4 text-[13px] text-text-muted">The coming week’s forecast is unavailable. Expired forecast dates are not shown as upcoming days.</p>;
      })(),

      planner: !activeStale && forecastPanelSlot ? <div data-house-section="planner">{forecastPanelSlot}</div> : null,

      packing: !activeStale && activePackingNotes.length > 0 ? (
        <div data-house-section="packing">
          <DisclosurePanel title="What to pack" id="section-packing">
            <ul className="space-y-1.5 text-[14px] leading-[1.7] text-text-body">
              {activePackingNotes.map((note, index) => (
                <li key={index} className="flex items-baseline gap-2.5">
                  <span aria-hidden className="h-1.5 w-1.5 shrink-0 translate-y-[-1px] rounded-full bg-gold" />
                  <span>{note}</span>
                </li>
              ))}
            </ul>
          </DisclosurePanel>
        </div>
      ) : null,
      }).map(({ slot, value }) => <Fragment key={slot}>{value}</Fragment>)}
    </div>
  );

  /* ─── The spot house: sport-aware environmental + server sections ─── */

  const spotPanel = (
    <div className="py-8 md:py-10">
      {orderLocationHouseSlots<"spot", ReactNode>("spot", {
      description: <SpotOverview description={description} metadata={locationMetadata} facts={[
        { label: "Bottom", value: bottomType },
        { label: "Suitability", value: difficultyLevel },
        { label: "Lifeguards", value: lifeguarded },
      ]} />,
      "local-detail": activeBriefingInput || spotLocalSlot ? (
        <div data-house-section="local-detail" className="space-y-8">
          {activeBriefingInput && <LocalBriefing key={activeSport} input={activeBriefingInput} />}
          {spotLocalSlot}
        </div>
      ) : null,
      community: spotCommunitySlot,
      environment: hasEnvironmental ? (
        <div data-house-section="environment">
          <DisclosurePanel title="Air & water" id="section-environmental">
            <div className="space-y-6">
              {hasAirQuality && airQuality && <AirQualityPanel data={airQuality} />}
              {hasWaterQuality && (
                <WaterQualitySection
                  waterQuality={waterQuality}
                  satelliteWQ={satelliteWQ}
                  currentHourWeather={currentHour?.weather ?? null}
                  currentHourMarine={currentHour?.marine ?? null}
                />
              )}
            </div>
          </DisclosurePanel>
        </div>
      ) : null,
      }).map(({ slot, value }) => <Fragment key={slot}>{value}</Fragment>)}
    </div>
  );

  /* ─── Season house: sport-aware heatmap + server sections ─── */

  const seasonPanel = (
    <div className="py-5 md:py-8">
      <SeasonOverview datasets={activeHeatmapDatasets} />
      {orderLocationHouseSlots<"season", ReactNode>("season", {
      "season-pattern": activeHeatmapDatasets.length > 0 ? (
        <DisclosurePanel title="Season pattern" id="section-season" defaultOpen>
          <SeasonHeatmap
            key={activeSport}
            datasets={activeHeatmapDatasets}
            spotName={spotName}
          />
        </DisclosurePanel>
      ) : null,
      additional: seasonPanelSlot,
      }).map(({ slot, value }) => <Fragment key={slot}>{value}</Fragment>)}
    </div>
  );

  const forecastSummary = <div className="pt-5 md:pt-6">
      <ForecastStatus lastUpdated={activeUpdated} isStale={activeStale} scoreUnavailable={!isSurf && !sportDetail} forecastIssuedLabel={activeStale && activeBadgeIsGapForecast(badges, activeSport) ? gapForecast?.issuedLabel : null} />
      {orderLocationHouseSlots<"aboveTabs", ReactNode>("aboveTabs", {
        "current-score": (
          <>
            <div data-house-section="current-score">
              <VerdictBlock headerActions={<WindUnitControl />} defaultSport={defaultSport} perSport={verdictsWithGapForecast(perSportVerdict, badges, gapForecast)} enhancements={activeStale ? {} : universalEnhancements} perSportLuck={perSportLuck} availableSports={availableSports} />
            </div>
            {areaSlot ? <div data-house-section="better-nearby">{areaSlot}</div> : null}
          </>
        ),
        safety: (
          <div id="spot-safety" data-house-section="safety" className="scroll-mt-28">
            <SafetyHeadsUp defaultSport={defaultSport} perSport={perSportSafety} availableSports={availableSports} />
          </div>
        ),
        "factor-summary": !activeStale && currentExplanation ? (
          <section className="pt-4" data-house-section="factor-summary">
            <FactorHeadlineSummary explanation={currentExplanation} />
          </section>
        ) : null,
        disclaimer: (
          <section className="pt-4">
            <SafetyDisclaimer />
          </section>
        ),
      }).map(({ slot, value }) => <Fragment key={slot}>{value}</Fragment>)}
      {!activeStale && <CoastalSportFocus sport={activeSport} swim={swimEnhancements} sup={supEnhancements} metadata={locationMetadata} waterQuality={waterQuality || satelliteWQ ? <WaterQualitySection compact waterQuality={waterQuality} satelliteWQ={satelliteWQ} currentHourWeather={null} currentHourMarine={null} /> : <p className="text-amber-800">Water-quality evidence is unavailable. Check the current local bathing-water advice.</p>} />}
  </div>;

  const panels: Record<HouseId, ReactNode> = {
    forecast: <>{forecastSummary}{forecastPanel}</>,
    spot: spotPanel,
    season: seasonPanel,
  };

  return (
    <HouseTabs panels={panels} toolbar={<SportSelector badges={badges} defaultSport={defaultSport} />} />
  );
}
